// A circle's invitations and members with desyr ID (37-desyr-id-invitations.md; 94-services-api.md,
// desyr ID, "Under the circle's admin address"): the calls with the ID token, the passkey sign-in
// they need (Behaviour 3), the service's codes in 37's sentences, and the passkey card's day.
import type { MatrixClient } from 'matrix-js-sdk';
import { useSyncExternalStore } from 'react';
import { adminWords } from '../account/words';
import { config } from '../core/config';
import { t } from '../i18n';
import { companyOf } from '../session/admin';
import { idCall, type Circle } from './api';
import { useCircles } from './circles';
import { startSignIn } from './oidc';
import { pressed } from './pressed';
import { confirmFirst, stayed } from './StepUp';
import { keepAction, readAction, readId } from './store';

export interface Member {
  /** The desyr ID. */
  id: string;
  name: string;
  mxid: string;
  role: Circle['role'];
  joinedAt: string;
  hasPasskey: boolean;
  /** While their removal runs (v1.58.16); they leave the list when it's done. */
  removing?: boolean;
}
export interface Sent {
  id: string;
  email: string;
  name: string;
  mxid: string;
  role: 'admin' | 'member';
  language: 'de' | 'en';
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  status: 'open' | 'accepted' | 'declined' | 'revoked' | 'expired';
}
export interface Seats {
  /** null: no limit. */
  total: number | null;
  /** Members plus open invitations. */
  used: number;
}

/** A role in words: "Owner", "Admin" or "Member". */
export const roleWord = (role: Circle['role']) => t(role === 'owner' ? 'id.owner' : role === 'admin' ? 'id.admin' : 'id.member');

/** The stored sign-in doesn't do for a change: no passkey, or one longer than 15 minutes ago (Behaviour 3.1). */
export const staleSignIn = (id = readId(), now = Date.now()) => !id?.passkey || (id.authTime !== undefined && now - id.authTime * 1000 >= 15 * 60_000);

/**
 * The passkey sign-in again (Behaviour 3.1), back to this page; a change waiting for it is kept with
 * its dialog and typed fields, opened again afterwards and sent only when pressed again.
 */
export function confirmPasskey(page: string, dialog?: Record<string, string>): Promise<void> {
  keepAction({ page, dialog, at: Date.now() });
  return startSignIn(config().desyrId!, location.pathname + location.search, { passkey: true });
}

/** Back from the passkey sign-in still without "mfa" (the desyr ID has no passkey yet): the card, never another sign-in. */
export function noPasskeyYet(id = readId(), action = readAction()): boolean {
  return !!action && !!id && !id.passkey && (id.authTime ?? 0) * 1000 >= action.at - 1000;
}

let refused = false;
const listeners = new Set<() => void>();
/** The Circle group's note shows in place of the pages: no passkey this sign-in, or the service said so for a list. */
export const usePasskeyNote = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => void listeners.delete(l)),
    () => refused || staleSignIn(readId(), 0),
  );

const ownSignIn = (e: unknown) => {
  const { status, code } = e as { status?: number; code?: string };
  return status === 403 && (code === 'desyr.id.passkey_needed' || code === 'desyr.id.sign_in_again');
};

/**
 * A call to the circle's invitation and member routes (People's). A change signs in again first
 * when the stored sign-in already shows it's needed, or after the service refused the caller's
 * sign-in, keeping `dialog`, after the in-app step (v1.58.36; cancelled there it rejects with
 * `stayed`); a list so refused shows the Circle group's note. When desyr ID's own session is over
 * (36, Behaviour 1.4), People is the person's action that needs it: desyr ID's sign-in in this
 * window, once, back here at People (with a change's `dialog`, sent only when pressed again).
 */
export async function circleCall<T>(client: MatrixClient, path: string, init: { method?: string; body?: unknown; dialog?: Record<string, string> } = {}): Promise<T> {
  const change = (init.method ?? 'GET') !== 'GET';
  const again = async (): Promise<never> => {
    if (!(await confirmFirst(init.dialog?.kind === 'invite', () => confirmPasskey('people', init.dialog)))) throw stayed;
    return new Promise<never>(() => {});
  };
  if (change && staleSignIn()) return again();
  try {
    return await pressed(
      () => idCall<T>(config().desyrId!, path, { method: init.method, body: init.body, base: companyOf(client)?.admin }),
      () => keepAction({ page: 'people', dialog: init.dialog, at: Date.now() }),
    );
  } catch (e) {
    if (ownSignIn(e) && change) return again();
    if (ownSignIn(e)) (refused = true), listeners.forEach((l) => l());
    throw e;
  }
}

/** The service's refusals in 37's sentences (Behaviours 1–2); any other code by its kind. */
export const idWords = (e: unknown, p: { email?: string; name?: string; circle: string }) => {
  const params = { email: p.email ?? '', name: p.name ?? '', circle: p.circle };
  return adminWords(e, 'save', {
    'desyr.invite.already_member': t('id.alreadyMember', params),
    'desyr.invite.already_invited': t('id.alreadyInvited', params),
    'desyr.invite.name_taken': t('id.nameTaken', params),
    'desyr.invite.no_seat': t('id.noSeat', params),
    'desyr.invite.bad_email': t('id.badEmail'),
    'desyr.invite.hosted_email': t('id.hostedEmail'),
    'desyr.invite.too_many_open': t('id.tooManyOpen', params),
    'desyr.invite.too_many': t('id.tooMany'),
    'desyr.invite.bad_name': t('id.badName'),
    'desyr.invite.bad_role': t('id.badRole'),
    'desyr.invite.bad_language': t('id.badLanguage'),
    'desyr.invite.busy': t('id.busy'),
    'desyr.invite.not_found': t('id.notOpen'),
    'desyr.invite.not_yours': t('id.notYours'),
    'desyr.id.unavailable': t('id.unavailable'),
    'desyr.id.passkey_needed': t('id.needsPasskey', params),
    'desyr.member.not_allowed': t('id.onlyOwner'),
  });
};

const PROMPTED = 'desyr_passkey_prompted';
/** This device's day as YYYY-MM-DD. */
const dayOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** The passkey card at most once a day on this device (Behaviour 3.4): true when not shown yet today, and today is kept. */
export function promptToday(now = new Date()): boolean {
  try {
    if (localStorage.getItem(PROMPTED) === dayOf(now)) return false;
    localStorage.setItem(PROMPTED, dayOf(now));
  } catch {
    // storage refused: the card shows on this page only
  }
  return true;
}

let card: boolean | undefined;
/** The circle the passkey card names (the first one managed) while the card shows on this page; decided once per page. */
export function usePasskeyCard(): Circle | undefined {
  const { answer } = useCircles();
  const managed = answer && !answer.hasPasskey ? answer.circles.find((c) => c.role !== 'member') : undefined;
  if (managed && card === undefined) card = promptToday();
  return card ? managed : undefined;
}
